import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Tooltip, Typography } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import {
  PaymentRequestConnection,
  PaymentRequest,
  OrderDirection,
  Maybe,
  CommunicationType,
  PaymentRequestCommunicationStatus,
  PaymentRequestStatus,
  SubType,
} from '../gql-types.generated';
import { paymentStatusDisplay } from '../util/PaymentStatus';
import {
  DataGridProProps,
  GridApiRef,
  GridColDef,
  GridColumnOrderChangeParams,
  GridColumnVisibilityModel,
  GridRenderCellParams,
  GridRowModel,
  GridRowParams,
  GridSelectionModel,
} from '@mui/x-data-grid-pro';
import { DataList } from './DataList';
import { DateTime } from 'luxon';
import CommunicationUnsentIcon from '../CommunicationUnsent.svg';
import LoadingMask from './LoadingMask';

/**
 * TruncatedTextWithTooltip component
 * Displays text with an ellipsis when truncated and shows a tooltip only when the text is actually truncated
 */
const TruncatedTextWithTooltip: React.FC<{ value: string; align?: 'left' | 'right' | 'center' }> = ({ value, align = 'left' }) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const element = textRef.current;
    if (!element) {
      return undefined;
    }

    const checkTruncation = () => {
      if (textRef.current) {
        setIsTruncated(textRef.current.scrollWidth > textRef.current.clientWidth);
      }
    };

    checkTruncation();

    const resizeObserver = new ResizeObserver(() => {
      checkTruncation();
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [value]);

  return (
    <Tooltip title={value} placement="top" disableHoverListener={!isTruncated}>
      <span
        ref={textRef}
        style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', textAlign: align }}
      >
        {value}
      </span>
    </Tooltip>
  );
};

/**
 * SentToCell component
 * Displays the "Sent To" cell with truncated text, tooltip, and optional unsent icon
 */
interface SentToCellProps {
  value: string;
  showUnsentIcon: boolean;
  classes: Record<string, string>;
}

const SentToCell: React.FC<SentToCellProps> = ({ value, showUnsentIcon, classes }) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const element = textRef.current;
    if (!element) {
      return undefined;
    }

    const checkTruncation = () => {
      if (textRef.current) {
        setIsTruncated(textRef.current.scrollWidth > textRef.current.clientWidth);
      }
    };

    checkTruncation();

    const resizeObserver = new ResizeObserver(() => {
      checkTruncation();
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [value]);

  return (
    <div className={classes.communicationContainer}>
      <Tooltip title={value || ''} placement="top" disableHoverListener={!isTruncated}>
        <Typography
          ref={textRef}
          variant="inherit"
          className={`${classes.communicationText} ${showUnsentIcon ? classes.unsentMaxWidthText : undefined}`}
        >
          {value}
        </Typography>
      </Tooltip>
      {showUnsentIcon && <img src={CommunicationUnsentIcon} alt="not sent" className={classes.communicationUnsentIcon} />}
    </div>
  );
};

const useStyles = makeStyles(() =>
  createStyles({
    communicationContainer: {
      display: 'flex',
      width: '100%',
    },
    communicationText: {
      maxWidth: '100%',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
    },
    communicationUnsentIcon: {
      marginLeft: 8,
      width: '18px',
    },
    unsentMaxWidthText: {
      maxWidth: 'calc(100% - 27px)',
    },
    ellipsisText: {
      maxWidth: '100%',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
    },
  }),
);

// Table for the payment requests
interface PaymentRequestListProps {
  defaultCurrency?: string | null;
  paymentRequestConnection: PaymentRequestConnection;
  paymentRequests: PaymentRequest[] | undefined;
  direction: OrderDirection;
  field: string;
  handleSort: (arrowDirection: OrderDirection, fieldName: string) => void;
  loadPage: (endEdge: string) => void;
  handleSelectedRecordsChange: (records: PaymentRequest[] | undefined) => void;
  handleViewPaymentRequest: (record: PaymentRequest | undefined) => void;
  isLoadingPaymentRequests: boolean;
  gridApiRef: GridApiRef;
  isRemindFromMenuInFlight: boolean;
  isCustomerEnabled: boolean;
}

const PaymentRequestList: React.FC<PaymentRequestListProps> = props => {
  const {
    defaultCurrency,
    paymentRequestConnection,
    paymentRequests,
    handleViewPaymentRequest,
    loadPage,
    handleSelectedRecordsChange,
    isLoadingPaymentRequests,
    gridApiRef,
    isRemindFromMenuInFlight,
    isCustomerEnabled,
  } = props;
  const classes = useStyles();

  useEffect(() => {
    if (!isLoadingPaymentRequests) {
      gridApiRef?.current?.setSelectionModel([]);
    }
  }, [isLoadingPaymentRequests]);

  const columnOrderStorageKey = 'paymentsListColumnOrder';
  const visibilityStorageKey = 'paymentsListColumnVisibility';

  const savedOrder = useMemo(() => {
    try {
      const raw = localStorage.getItem(columnOrderStorageKey);
      if (!raw) return undefined;
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr : undefined;
    } catch {
      return undefined;
    }
  }, []);

  const savedVisibilityModel = useMemo(() => {
    try {
      const raw = localStorage.getItem(visibilityStorageKey);
      if (!raw) return undefined;
      const obj = JSON.parse(raw);
      return obj && typeof obj === 'object' && !Array.isArray(obj) ? obj : undefined;
    } catch {
      return undefined;
    }
  }, []);

  const handleColumnOrderChange = (_params: GridColumnOrderChangeParams) => {
    if (!gridApiRef.current) return;
    const ordered = gridApiRef.current.getAllColumns().map(c => c.field);
    localStorage.setItem(columnOrderStorageKey, JSON.stringify(ordered));
  };

  const handleColumnVisibilityModelChange = (model: GridColumnVisibilityModel) => {
    localStorage.setItem(visibilityStorageKey, JSON.stringify(model));
  };

  const getPaymentRequestById = (id: string) => {
    const foundPaymentRequest = paymentRequests?.find(paymentRequest => paymentRequest?.id === id);
    // eslint-disable-next-line no-underscore-dangle
    return foundPaymentRequest;
  };
  // Create proper rows for the data grid from the payment request data.
  const getPaymentRequestRows = () => {
    return paymentRequests?.map((row: Maybe<PaymentRequest>) => {
      const node = row;
      if (!node) {
        return {} as GridRowModel;
      }
      const communications = node.communications?.filter(c => c?.subType !== SubType.Preview);
      let communication = communications && communications.length > 0 ? communications[0] : undefined;
      const contact =
        communication && communication.communicationType === CommunicationType.Email
          ? communication.email
          : communication?.phoneNumber;

      node.communications?.forEach(comm => {
        const aDate = DateTime.fromISO(communication?.requestTimestamp || new Date(0).toISOString());
        const bDate = DateTime.fromISO(comm?.requestTimestamp || new Date(0).toISOString());
        if (bDate > aDate && comm?.subType !== SubType.Preview) {
          communication = comm;
        }
      });

      // checking whether we have non sent and non preview emails
      const hasUnsentCommunication = communications?.filter(c => c?.status === PaymentRequestCommunicationStatus.Unsent).length > 0;
      const hasUnsentStatus =
        node?.status === PaymentRequestStatus.Unpaid ||
        node?.status === PaymentRequestStatus.PartiallyPaid ||
        node?.status === PaymentRequestStatus.Failed ||
        node?.status === PaymentRequestStatus.Canceled;
      // checking whether the unsent icon to show according to statuses and communication history
      const showUnsentIcon = hasUnsentCommunication && hasUnsentStatus;

      return {
        _raw: node,
        id: node.id,
        referenceNumber: node.referenceNumber?.trim(),
        statusRender: paymentStatusDisplay.find(item => {
          return item.name === node.status;
        })?.display,
        amount:
          typeof node.amount === 'number'
            ? new Intl.NumberFormat('en', {
                style: 'currency',
                currency: defaultCurrency || 'USD',
                currencyDisplay: 'symbol',
              }).format(node.amount / 100)
            : null,
        sentTo: node?.customer && node.customer?.name ? node.customer?.name || '' : contact,
        totalPaid:
          typeof node.totalPaid === 'number'
            ? new Intl.NumberFormat('en', {
                style: 'currency',
                currency: defaultCurrency || 'USD',
                currencyDisplay: 'symbol',
              }).format(node.totalPaid / 100)
            : null,
        totalRefunded:
          typeof node.totalRefunded === 'number'
            ? new Intl.NumberFormat('en', {
                style: 'currency',
                currency: defaultCurrency || 'USD',
                currencyDisplay: 'symbol',
              }).format(node.totalRefunded / 100)
            : null,
        createdDate: new Date(node.createdAt)?.toLocaleDateString(),
        dueDate: node.dueDate ? new Date(node.dueDate).toLocaleDateString() : '',
        discountAmount:
          typeof node.discountAmount === 'number'
            ? new Intl.NumberFormat('en', {
                style: 'currency',
                currency: defaultCurrency || 'USD',
                currencyDisplay: 'symbol',
              }).format(node.discountAmount / 100)
            : null,
        discountEndDate: node.discountEndDate ? new Date(node.discountEndDate).toLocaleDateString() : '',
        reminderSent: node.reminderSent ? 'Yes' : 'No',
        overdueReminderSent: node.overdueReminderSent ? 'Yes' : 'No',
        additionalInfo: node.additionalInfo || '',
        customerName: node?.customer?.name || '',
        customerNumber: node?.customer?.customerNumber || '',
        customerPONumber: node?.customerPONumber || '',
        invoiceNumber: node?.invoiceNumber || '',
        orderNumber: node?.orderNumber || '',

        date:
          communication && communication?.sentTimestamp
            ? new Date(communication.sentTimestamp).toLocaleDateString()
            : new Date(node.createdAt).toLocaleDateString(),
        showUnsentIcon,
      } as GridRowModel;
    }) as GridRowModel[];
  };
  const [paymentRequestRows, setPaymentRequestRows] = useState<GridRowModel[]>(getPaymentRequestRows());
  const [loadingPage, setLoadingPage] = useState(false);

  useEffect(() => {
    setLoadingPage(false);
    setPaymentRequestRows(getPaymentRequestRows());
  }, [paymentRequests]);

  const handlePageLoad = () => {
    if (!paymentRequestConnection) {
      // There is not connection to use for retrieving the next page
      return;
    }
    // No more pages to load, no need to send the load request
    if (!paymentRequestConnection.pageInfo.hasNextPage) {
      return;
    }
    if (!paymentRequestConnection.pageInfo.endCursor) {
      return;
    }
    setLoadingPage(true);
    loadPage(paymentRequestConnection.pageInfo.endCursor);
  };

  // Column titles for mapping and sorting
  const paymentRequestColumns: GridColDef[] = useMemo(
    () => [
      {
        // eslint-disable-next-line react/display-name
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Link
              underline={'hover'}
              href="#"
              className={classes.ellipsisText}
              onClick={(event: React.MouseEvent) => {
                // stopPropagation to stop click propagation through to the grid - so it does not toggle checkbox.
                event.stopPropagation();
                // eslint-disable-next-line no-underscore-dangle
                handleViewPaymentRequest(params?.row?._raw as PaymentRequest);
              }}
            >
              {params.value}
            </Link>
          );
        },
        resizable: false,
        field: 'referenceNumber',
        sortable: false,
        headerName: 'Reference #',
        flex: 0.19,
        minWidth: 130,
      },
      {
        // eslint-disable-next-line react/display-name
        renderCell: (params: GridRenderCellParams) => <TruncatedTextWithTooltip value={params.value || ''} />,
        resizable: false,
        field: 'statusRender',
        sortable: false,
        headerName: 'Status',
        flex: 0.13,
        minWidth: 160,
      },
      {
        // eslint-disable-next-line react/display-name
        renderCell: (params: GridRenderCellParams) => {
          const { showUnsentIcon } = params?.row;
          return <SentToCell value={params.value || ''} showUnsentIcon={showUnsentIcon} classes={classes} />;
        },
        resizable: false,
        field: 'sentTo',
        sortable: false,
        headerName: 'Sent To',
        flex: 0.32,
        minWidth: 130,
      },
      {
        resizable: false,
        field: 'date',
        sortable: false,
        headerName: 'Last Requested',
        flex: 0.15,
        minWidth: 110,
      },
      {
        // eslint-disable-next-line react/display-name
        renderCell: (params: GridRenderCellParams) => <TruncatedTextWithTooltip value={params.value || ''} align="right" />,
        resizable: false,
        field: 'amount',
        sortable: false,
        headerName: 'Amount',
        headerAlign: 'right',
        align: 'right',
        flex: 0.13,
        minWidth: 130,
      },
      {
        resizable: false,
        field: 'totalPaid',
        sortable: false,
        headerName: 'Total Paid',
        headerAlign: 'right',
        align: 'right',
        flex: 0.13,
        minWidth: 130,
      },
      {
        resizable: false,
        field: 'createdDate',
        sortable: false,
        headerName: 'Created Date',
        flex: 0.13,
        minWidth: 130,
      },
      {
        resizable: false,
        field: 'dueDate',
        sortable: false,
        headerName: 'Due Date',
        flex: 0.13,
        minWidth: 130,
      },
      {
        resizable: false,
        field: 'discountAmount',
        sortable: false,
        headerName: 'Discount Amount',
        headerAlign: 'right',
        align: 'right',
        flex: 0.13,
        minWidth: 130,
      },
      {
        resizable: false,
        field: 'discountEndDate',
        sortable: false,
        headerName: 'Discount End Date',
        flex: 0.13,
        minWidth: 130,
      },
      {
        resizable: false,
        field: 'reminderSent',
        sortable: false,
        headerName: 'Reminder Sent',
        flex: 0.13,
        minWidth: 130,
      },
      {
        resizable: false,
        field: 'overdueReminderSent',
        sortable: false,
        headerName: 'Overdue Reminder Sent',
        flex: 0.13,
        minWidth: 130,
      },
      {
        resizable: false,
        field: 'totalRefunded',
        sortable: false,
        headerName: 'Total Refunded',
        headerAlign: 'right',
        align: 'right',
        flex: 0.13,
        minWidth: 130,
      },
      {
        // eslint-disable-next-line react/display-name
        renderCell: (params: GridRenderCellParams) => <TruncatedTextWithTooltip value={params.value || ''} />,
        resizable: false,
        field: 'additionalInfo',
        sortable: false,
        headerName: 'Additional Info',
        flex: 0.13,
        minWidth: 130,
      },
      {
        // eslint-disable-next-line react/display-name
        renderCell: (params: GridRenderCellParams) => <TruncatedTextWithTooltip value={params.value || ''} />,
        resizable: false,
        field: 'customerName',
        hide: !isCustomerEnabled,
        sortable: false,
        headerName: 'Customer Name',
        flex: 0.13,
        minWidth: 160,
      },
      {
        // eslint-disable-next-line react/display-name
        renderCell: (params: GridRenderCellParams) => <TruncatedTextWithTooltip value={params.value || ''} />,
        resizable: false,
        field: 'customerNumber',
        hide: !isCustomerEnabled,
        sortable: false,
        headerName: 'Customer Number',
        flex: 0.13,
        minWidth: 160,
      },
      {
        // eslint-disable-next-line react/display-name
        renderCell: (params: GridRenderCellParams) => <TruncatedTextWithTooltip value={params.value || ''} />,
        resizable: false,
        field: 'customerPONumber',
        sortable: false,
        headerName: 'Customer PO Number',
        flex: 0.13,
        minWidth: 160,
      },
      {
        // eslint-disable-next-line react/display-name
        renderCell: (params: GridRenderCellParams) => <TruncatedTextWithTooltip value={params.value || ''} />,
        resizable: false,
        field: 'invoiceNumber',
        sortable: false,
        headerName: 'Invoice Number',
        flex: 0.13,
        minWidth: 130,
      },
      {
        // eslint-disable-next-line react/display-name
        renderCell: (params: GridRenderCellParams) => <TruncatedTextWithTooltip value={params.value || ''} />,
        resizable: false,
        field: 'orderNumber',
        sortable: false,
        headerName: 'Order Number',
        flex: 0.13,
        minWidth: 130,
      },
    ],
    [],
  );

  const onSelectionModelChange = (selectionModel: GridSelectionModel) => {
    if (selectionModel.length > 0) {
      const selectedRecords: PaymentRequest[] = [];
      selectionModel.forEach(m => {
        const request = getPaymentRequestById(m as string);
        if (request) {
          selectedRecords.push(request);
        }
      });
      handleSelectedRecordsChange(selectedRecords.length > 0 ? selectedRecords : undefined);
    } else {
      handleSelectedRecordsChange(undefined);
    }
  };

  // eslint-disable-next-line no-underscore-dangle
  const onRowDoubleClick = (params: GridRowParams) => handleViewPaymentRequest(params?.row?._raw as PaymentRequest);

  const gridOptions: DataGridProProps = {
    apiRef: gridApiRef,
    columns: paymentRequestColumns,
    rows: paymentRequestRows,
    'aria-label': 'Payment Requests Table',
    loading: loadingPage,
    checkboxSelection: true,
    onRowsScrollEnd: handlePageLoad,
    initialState: {
      columns: {
        orderedFields: savedOrder,
        columnVisibilityModel: {
          totalPaid: false,
          totalRefunded: false,
          discountAmount: false,
          discountEndDate: false,
          reminderSent: false,
          overdueReminderSent: false,
          createdDate: false,
          dueDate: false,
          additionalInfo: false,
          customerName: false,
          customerNumber: false,
          customerPONumber: false,
          invoiceNumber: false,
          orderNumber: false,
          ...savedVisibilityModel,
        },
      },
    },
    onColumnOrderChange: handleColumnOrderChange,
    onColumnVisibilityModelChange: handleColumnVisibilityModelChange,
    onSelectionModelChange,
    onRowDoubleClick,
  };

  return (
    <>
      <LoadingMask loading={isRemindFromMenuInFlight} />
      <DataList
        gridOptions={gridOptions}
        hasCheckbox
        cypressTag="payment-request-table-body"
        isSkeletonHidden={!isLoadingPaymentRequests}
        noDataText="No payment request data to show."
      />
    </>
  );
};

export default PaymentRequestList;
